import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useSection, useSeo } from "@/contexts/SiteContentContext";
import { Calendar, MapPin, Users, ChevronRight } from "lucide-react";

export default function Camps() {
  const c = useSection("camps");
  useSeo(c.seo_title, c.meta_description);
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    api
      .get("/camps")
      .then(({ data }) => setCamps(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = camps.filter((c) => (filterType === "all" ? true : c.type === filterType));

  const types = ["all", "ID Camp", "Showcase", "Selection Camp", "Clinic", "Tryout"];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-[#0A192F] text-white py-20 border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946]">{c.overline || "Events · 2026 Calendar"}</div>
          <h1
            data-testid="camps-page-title"
            className="font-display text-5xl lg:text-6xl uppercase tracking-tighter mt-4"
          >
            {c.title || "CBR Camps & Showcases"}
          </h1>
          <p className="mt-6 text-white/70 max-w-3xl">
            {c.subtitle || "ID camps, showcases, selection camps and clinics across Canada. Every event includes verified metric testing and scout coverage."}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-wrap gap-2">
          {types.map((t) => (
            <button
              key={t}
              data-testid={`filter-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilterType(t)}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors ${
                filterType === t
                  ? "bg-[#0A192F] text-white"
                  : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"
              }`}
            >
              {t === "all" ? "All Events" : t}
            </button>
          ))}
        </div>
      </section>

      {/* Camps list */}
      <section className="bg-[#F8F9FA] py-16 min-h-[500px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="label-overline">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 p-12 text-center">
              <div className="font-display text-2xl uppercase tracking-tight">No camps found</div>
              <p className="text-sm text-slate-600 mt-2">Try another filter or check back soon.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {filtered.map((c) => (
                <Link
                  key={c.id}
                  to={`/camps/${c.slug}`}
                  data-testid={`camp-card-${c.slug}`}
                  className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#0A192F] transition-colors group flex flex-col md:flex-row"
                >
                  <div
                    className="md:w-2/5 h-56 md:h-auto bg-slate-900 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${c.cover_image_url || ""})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent" />
                    <span className="absolute top-3 left-3 bg-[#E63946] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1">
                      {c.type}
                    </span>
                  </div>
                  <div className="flex-1 p-6 flex flex-col">
                    <div className="label-overline">{c.province}</div>
                    <h3 className="font-display text-2xl uppercase tracking-tight mt-2 leading-tight">{c.title}</h3>
                    <p className="text-sm text-slate-600 mt-3 line-clamp-2">{c.description}</p>
                    <div className="mt-auto pt-6 grid grid-cols-2 gap-4 text-xs">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4 text-[#E63946]" />
                        {new Date(c.start_date).toLocaleDateString("en-CA", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-[#E63946]" />
                        {c.city}, {c.province}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Users className="w-4 h-4 text-[#E63946]" />
                        {c.capacity} spots
                      </div>
                      <div className="font-data text-xl text-[#0A192F] text-right">${c.price}</div>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#0A192F] group-hover:text-[#E63946]">
                      View Details <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
